import { action, observable, runInAction, computed } from 'mobx';
import { findIndex, sortBy, head } from 'lodash';
import API from '../core/api';

import BGCheckAccount from '../models/background-check/bg-check-account';
import BgCheckReport from '../models/background-check/bg-check-report';
import BgCheckOrder from '../models/background-check/bg-check-order';
import BgCheckProgramMemberEmails from '../models/background-check/bg-check-program-member-emails';
import BgCheckPackage from '../models/background-check/bg-check-package';
import OrganizationStore from './organization-store';

export default class BackgroundCheckStore {
  @observable accounts: BGCheckAccount[] = [];
  @observable loadingBGCheckAccounts = false;
  @observable reports: Map<number, BgCheckReport[]> = new Map();
  @observable order: BgCheckOrder = new BgCheckOrder();
  @observable packages: BgCheckPackage[] = [];
  readonly organizationStore: OrganizationStore;

  constructor(organizationStore: OrganizationStore) {
    this.organizationStore = organizationStore;
  }

  @computed
  get accountsSortedByPartner() {
    return sortBy(this.accounts, 'partner');
  }

  // TODO: only 1 for now so grab head (first) but setup for multiple in future
  @computed
  get account() {
    return head(this.accountsSortedByPartner);
  }

  @action
  clearAccounts() {
    this.accounts = [];
  }

  @action
  updateAccount(account: BGCheckAccount) {
    const existingIndex = findIndex(this.accounts, (acct) => acct.id === account.id);
    if (existingIndex === -1) {
      this.accounts.push(account);
    } else {
      this.accounts[existingIndex] = account;
    }
    return account;
  }

  @action
  async loadAccounts(organizationId: number) {
    this.loadingBGCheckAccounts = true;
    const result = await API.queryList('backgroundCheckAccounts', BGCheckAccount, { organizationId });
    return await runInAction(() => {
      this.loadingBGCheckAccounts = false;
      return (this.accounts = result.data);
    });
  }

  getReport(programMemberId: number) {
    return this.reports.get(programMemberId);
  }

  @action
  async checkForReport(programMemberId: number) {
    if (this.reports.has(programMemberId)) {
      return this.reports.get(programMemberId);
    }

    const result = await API.queryList('backgroundCheckReport', BgCheckReport, {
      programMemberId,
      organizationId: this.organizationStore.selectedOrganizationId,
    });

    return runInAction(() => {
      const data = result.data || [];
      this.reports.set(programMemberId, data);
      return data;
    });
  }

  @action
  async toggleEnabled(accountId: number, enabled: boolean) {
    const result = await API.mutation('toggleEnabled', BGCheckAccount, { accountId, enabled });
    return this.updateAccount(result.data);
  }

  @action
  async openReportOrderModal(programMemberId: number, firstName: string, lastName: string) {
    const result = await API.query('backgroundCheckProgramMemberEmails', ['emails'], {
      programMemberId,
    });

    this.order.programMemberId = programMemberId;
    this.order.firstName = firstName;
    this.order.lastName = lastName;
    this.order.emails = result.data['emails'];

    if (this.order.emails.length === 1) {
      this.order.selectedEmail = this.order.emails[0];
      this.showReportOrderOptions();
    } else {
      this.order.showSelectEmail = true;
    }

    this.order.showOrderModal = true;
  }

  @action
  closeReportOrderModal() {
    this.order.reset();
  }

  @action
  setReportOrderEmail(email: string) {
    this.order.selectedEmail = email;
  }

  @action
  setReportOrderPackage(packageSlug: string) {
    this.order.selectedPackage = packageSlug;
  }

  @action
  setReportOrderBillCandidate(billCandidate: boolean) {
    this.order.billCandidate = billCandidate;
  }

  @action
  showReportOrderOptions() {
    this.getPackages(this.account.id);
    this.order.showSelectEmail = false;
    this.order.showSelectBackgroundCheck = true;
  }

  @action
  async getPackages(accountId: number) {
    const result = await API.queryList('packages', BgCheckPackage, { accountId });

    return runInAction(() => (this.packages = result.data));
  }

  @action
  async orderReport(organizationId: number) {
    const payload = {
      organizationId,
      programMemberId: this.order.programMemberId,
      firstName: this.order.firstName,
      lastName: this.order.lastName,
      email: this.order.selectedEmail,
      package: this.order.selectedPackage,
      billCandidate: false, // this.order.billCandidate // TODO: turn back on when we decide to to bill candidate
    };

    const result = await API.mutation('orderReport', BgCheckReport, payload);

    return runInAction(() => {
      const reports = this.reports.get(this.order.programMemberId);
      reports.push(result.data);

      return result.data;
    });
  }
}
